import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, MessageCircle, Send, CheckCircle2, Loader2, PhoneIncoming } from "lucide-react";
import { Reveal, Chapter } from "./Reveal";
import { CallbackModal } from "./CallbackModal";
import { COMPANY, WHATSAPP_URL, API, SERVICES } from "../constants";

const OBJEKTARTEN = [
  "Bürogebäude",
  "Praxis",
  "Kanzlei",
  "Ladenlokal",
  "Gewerbeimmobilie",
  "Wohnanlage / Eigentümergemeinschaft",
  "Baustelle / Bauvorhaben",
  "Sonstiges",
];

const GROESSEN = ["bis 200 m²", "200 – 500 m²", "500 – 1.000 m²", "1.000 – 3.000 m²", "über 3.000 m²"];

const EMPTY = {
  unternehmen: "",
  ansprechpartner: "",
  telefon: "",
  email: "",
  objektart: "",
  leistung: "",
  objektgroesse: "",
  nachricht: "",
  datenschutz: false,
  website: "",
};

const inputCls =
  "w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm text-ink placeholder:text-gray-400 focus:border-brand focus:ring-2 focus:ring-brand/20 focus:outline-none transition-colors duration-200";

const Field = ({ label, required, children, id }) => (
  <div>
    <label htmlFor={id} className="block text-sm font-medium text-ink mb-1.5">
      {label} {required && <span className="text-brand">*</span>}
    </label>
    {children}
  </div>
);

export const ContactSection = () => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [callbackOpen, setCallbackOpen] = useState(false);

  useEffect(() => {
    const onPrefill = (e) => setForm((f) => ({ ...f, leistung: e.detail }));
    window.addEventListener("emt:prefill-service", onPrefill);
    return () => window.removeEventListener("emt:prefill-service", onPrefill);
  }, []);

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      setSent(true);
      setForm(EMPTY);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: "lead_form_submit" });
      const conversionLabel = process.env.REACT_APP_GADS_CONVERSION_LABEL;
      let consentGranted = false;
      try {
        consentGranted = JSON.parse(localStorage.getItem("emt-consent-v1") || "{}").marketing === true;
      } catch {}
      if (conversionLabel && consentGranted && typeof window.gtag === "function") {
        window.gtag("event", "conversion", {
          send_to: `AW-18510592406/${conversionLabel}`,
          transaction_id: res.data.request_id,
        });
      }
    } catch (err) {
      toast.error("Ihre Anfrage konnte leider nicht gesendet werden. Bitte versuchen Sie es erneut oder rufen Sie uns direkt an.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="kontakt" className="bg-surface py-16 md:py-24" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <Chapter number="07" label="Kontakt" />
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-ink max-w-2xl">
            Jetzt kostenlose Objektbesichtigung vereinbaren
          </h2>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-5 gap-8">
          <Reveal className="lg:col-span-2">
            <div className="relative h-full rounded-3xl bg-navy text-white overflow-hidden">
              <div className="noise-overlay" />
              <img
                src="https://images.pexels.com/photos/7792841/pexels-photo-7792841.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Persönliche Übergabe – EMT-Reinigung als zuverlässiger Partner für Unternehmen"
                className="w-full h-44 object-cover"
                loading="lazy"
              />
              <div className="relative p-8">
                <h3 className="font-heading text-2xl font-semibold" data-testid="contact-info-title">
                  {COMPANY.name}
                </h3>
                <address className="not-italic mt-6 space-y-4 text-sm text-white/80">
                  <p className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-ice shrink-0 mt-0.5" />
                    <span>
                      {COMPANY.street}
                      <br />
                      {COMPANY.city}
                    </span>
                  </p>
                  <p className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-ice shrink-0" />
                    <a href={COMPANY.phoneHref} className="hover:text-white transition-colors" data-testid="contact-phone-link">
                      {COMPANY.phoneDisplay}
                    </a>
                  </p>
                  <p className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-ice shrink-0" />
                    <a href={`mailto:${COMPANY.email}`} className="hover:text-white transition-colors break-all" data-testid="contact-email-link">
                      {COMPANY.email}
                    </a>
                  </p>
                </address>
                <div className="mt-8 flex flex-col gap-3">
                  <a
                    href={COMPANY.phoneHref}
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-navy font-semibold px-6 py-3.5 transition-all duration-200 hover:scale-[1.02]"
                    data-testid="contact-cta-call"
                  >
                    <Phone className="w-4 h-4" />
                    Jetzt anrufen
                  </a>
                  <a
                    href={WHATSAPP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-conv hover:bg-conv-dark text-white font-semibold px-6 py-3.5 transition-all duration-200 hover:scale-[1.02]"
                    data-testid="contact-cta-whatsapp"
                  >
                    <MessageCircle className="w-4 h-4" />
                    WhatsApp-Anfrage
                  </a>
                  <div className="border-t border-white/15 pt-4 mt-1">
                    <button
                      onClick={() => setCallbackOpen(true)}
                      className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-white/25 text-white font-semibold px-6 py-3.5 transition-all duration-200 hover:bg-white/10"
                      data-testid="contact-cta-callback"
                    >
                      <PhoneIncoming className="w-4 h-4" />
                      Wir rufen Sie zurück
                    </button>
                    <p className="mt-2.5 text-center text-xs text-white/60">
                      Außerhalb der Erreichbarkeit: Wunschzeitraum wählen, wir melden uns.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-3">
            <div className="h-full bg-white border border-gray-200 rounded-3xl p-7 sm:p-9">
              {sent ? (
                <div className="h-full flex flex-col items-start justify-center py-10" data-testid="form-success">
                  <CheckCircle2 className="w-14 h-14 text-conv" strokeWidth={1.5} />
                  <h3 className="font-heading text-2xl font-semibold text-ink mt-6">
                    Vielen Dank für Ihre Anfrage!
                  </h3>
                  <p className="mt-3 text-mutedtext leading-relaxed max-w-md">
                    Ihre Anfrage ist bei uns eingegangen. Wir melden uns schnellstmöglich bei Ihnen,
                    um einen Termin für die kostenlose Objektbesichtigung zu vereinbaren.
                  </p>
                  <div className="mt-7 flex flex-wrap gap-3">
                    <a
                      href={WHATSAPP_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 rounded-full bg-conv text-white font-semibold px-6 py-3 text-sm"
                      data-testid="form-success-whatsapp"
                    >
                      <MessageCircle className="w-4 h-4" />
                      Direkt per WhatsApp schreiben
                    </a>
                    <button
                      onClick={() => setSent(false)}
                      className="rounded-full border border-gray-300 text-ink font-semibold px-6 py-3 text-sm hover:border-brand hover:text-brand transition-colors"
                      data-testid="form-success-new-request"
                    >
                      Neue Anfrage stellen
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="lead-form" noValidate={false}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Field label="Unternehmen" required id="f-unternehmen">
                      <input id="f-unternehmen" required value={form.unternehmen} onChange={set("unternehmen")} className={inputCls} placeholder="Muster GmbH" data-testid="form-input-unternehmen" />
                    </Field>
                    <Field label="Ansprechpartner" required id="f-ansprechpartner">
                      <input id="f-ansprechpartner" required value={form.ansprechpartner} onChange={set("ansprechpartner")} className={inputCls} placeholder="Vor- und Nachname" data-testid="form-input-ansprechpartner" />
                    </Field>
                    <Field label="Telefonnummer" required id="f-telefon">
                      <input id="f-telefon" type="tel" required value={form.telefon} onChange={set("telefon")} className={inputCls} placeholder="0511 / 123456" data-testid="form-input-telefon" />
                    </Field>
                    <Field label="E-Mail" required id="f-email">
                      <input id="f-email" type="email" required value={form.email} onChange={set("email")} className={inputCls} placeholder="name@unternehmen.de" data-testid="form-input-email" />
                    </Field>
                    <Field label="Art des Objekts" required id="f-objektart">
                      <select id="f-objektart" required value={form.objektart} onChange={set("objektart")} className={inputCls} data-testid="form-select-objektart">
                        <option value="" disabled>Bitte wählen</option>
                        {OBJEKTARTEN.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </Field>
                    <Field label="Gewünschte Leistung" required id="f-leistung">
                      <select id="f-leistung" required value={form.leistung} onChange={set("leistung")} className={inputCls} data-testid="form-select-leistung">
                        <option value="" disabled>Bitte wählen</option>
                        {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </Field>
                    <Field label="Ungefähre Objektgröße" id="f-groesse">
                      <select id="f-groesse" value={form.objektgroesse} onChange={set("objektgroesse")} className={inputCls} data-testid="form-select-groesse">
                        <option value="">Keine Angabe</option>
                        {GROESSEN.map((g) => <option key={g} value={g}>{g}</option>)}
                      </select>
                    </Field>
                    <div className="hidden" aria-hidden="true">
                      <label htmlFor="f-website">Website</label>
                      <input id="f-website" tabIndex="-1" autoComplete="off" value={form.website} onChange={set("website")} />
                    </div>
                  </div>
                  <div className="mt-5">
                    <Field label="Nachricht" id="f-nachricht">
                      <textarea id="f-nachricht" rows={4} value={form.nachricht} onChange={set("nachricht")} className={inputCls} placeholder="Beschreiben Sie kurz Ihr Objekt und Ihren Reinigungsbedarf …" data-testid="form-textarea-nachricht" />
                    </Field>
                  </div>
                  <label className="mt-6 flex items-start gap-3 cursor-pointer" data-testid="form-consent-label">
                    <input
                      type="checkbox"
                      required
                      checked={form.datenschutz}
                      onChange={set("datenschutz")}
                      className="mt-1 w-4 h-4 shrink-0"
                      data-testid="form-checkbox-datenschutz"
                    />
                    <span className="text-sm text-mutedtext leading-relaxed">
                      Ich habe die{" "}
                      <a href="/datenschutz" target="_blank" className="text-brand underline underline-offset-2">
                        Datenschutzerklärung
                      </a>{" "}
                      gelesen und stimme der Verarbeitung meiner Angaben zur Bearbeitung meiner Anfrage zu. <span className="text-brand">*</span>
                    </span>
                  </label>
                  <button
                    type="submit"
                    disabled={sending}
                    className="mt-7 w-full inline-flex items-center justify-center gap-2.5 rounded-full bg-conv hover:bg-conv-dark disabled:opacity-60 text-white font-semibold px-8 py-4 text-base transition-all duration-200 hover:scale-[1.01] shadow-[0_16px_32px_-12px_rgba(16,185,129,0.55)]"
                    data-testid="form-submit-button"
                  >
                    {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                    {sending ? "Wird gesendet …" : "Kostenloses Angebot anfordern"}
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
      <CallbackModal open={callbackOpen} onClose={() => setCallbackOpen(false)} />
    </section>
  );
};
